// ============================================================
// AS VISTAS DO PORTAL PARA A GENESIS (issue #584, migration 0065; Story 35.9, #649)
//
// Uma pessoa da Genesis (`admin`/`recruiter`) pode entrar no portal do cliente de duas
// formas: pelo PERFIL DE CLIENTE (membro de um cliente, com áreas, em
// `genesis_portal_access`) e como GESTOR DE CONTA (`customers.account_manager_user_id`:
// os clientes de que é gestora, com todas as áreas). O gestor de conta substituiu o
// suporte, que abria qualquer cliente ativo (decisão do owner de 2026-10-30). A vista em
// uso é por ABA: o web manda o cliente escolhido em cada pedido ao `/api/portal`, e o
// `requirePortalUser` confere-o. Daqui, a API e o web leem os mesmos nomes.
// ============================================================
import { z } from 'zod'

import { PortalAccessAreaIdsSchema, type CustomerStatus } from './dtos.ts'

/** O header com o cliente da vista, em todo `fetch` ao `/api/portal`. */
export const PORTAL_VIEW_HEADER = 'x-caliber-portal-customer'

/**
 * O mesmo cliente na query, SÓ em GET/HEAD: uma `<img>` (a foto) e um `<a>` (o
 * download do anexo) não levam header. Uma escrita exige o header.
 */
export const PORTAL_VIEW_QUERY = 'portalCustomer'

/**
 * Como o pedido entrou no portal:
 *   • `customer`        — o usuário do cliente, como sempre (o cliente vem de `users`);
 *   • `member`          — a Genesis no PERFIL DE CLIENTE, com as áreas dele;
 *   • `account_manager` — a Genesis como GESTORA DE CONTA de um cliente ativo, com
 *                         todas as áreas;
 *   • `owner`           — o OWNER (Story 35.10, #699) em qualquer cliente ativo, com
 *                         todas as áreas. Vem antes dos outros dois: um owner entra
 *                         sempre como owner, também no cliente do perfil dele.
 */
export const PORTAL_MODES = ['customer', 'member', 'account_manager', 'owner'] as const
export type PortalMode = (typeof PORTAL_MODES)[number]

/**
 * O que a pessoa da Genesis pode escolher, em `GET /api/me` (`portalViews`). `null`
 * para o usuário do cliente. Um cliente INATIVO não aparece em nenhuma das duas: o
 * `requirePortalUser` recusá-lo-ia. O cliente do perfil também não se repete em
 * `managedCustomers`: nele, a pessoa entra como membro.
 */
export interface PortalViews {
  clientCustomer: { id: string; name: string } | null
  /** Os clientes ativos de que a pessoa é gestora de conta, por nome. */
  managedCustomers: Array<{ id: string; name: string }>
  /**
   * A pessoa é OWNER (Story 35.10, #699): abre qualquer cliente ativo, e a lista é a
   * de `GET /api/customers`, que o admin já lê. A API manda-o sempre; opcional para as
   * vistas escritas antes dele, e a falta vale `false`. A tela de usuários lê-o também
   * para saber se quem vê é owner.
   */
  owner?: boolean
}

/**
 * O perfil de cliente no corpo do admin: o cliente e o acesso por áreas, com a
 * regra de `PortalAccessSchema` ("todas" não leva lista). Escrito por extenso porque
 * aquele é `.strict()` e recusaria o `customerId` numa interseção.
 */
export const GenesisPortalClientSchema = z
  .object({
    customerId: z.string().min(1),
    allAreas: z.boolean(),
    areaIds: PortalAccessAreaIdsSchema,
  })
  .strict()
  .refine((v) => !v.allAreas || v.areaIds.length === 0, {
    message: '"Todas as operações" não leva lista de operações.',
    path: ['areaIds'],
  })

/**
 * Corpo de `PUT /api/users/:id/genesis-portal` e resposta do `GET`. `client: null`
 * tira o perfil de cliente (e as áreas dele). O suporte saiu na Story 35.9: o acesso
 * a outros clientes é o de gestor de conta (`/api/users/:id/managed-customers`).
 */
export const GenesisPortalAccessSchema = z
  .object({ client: GenesisPortalClientSchema.nullable() })
  .strict()
export type GenesisPortalAccess = z.infer<typeof GenesisPortalAccessSchema>

/** Um cliente de que a pessoa é gestora de conta (`GET .../managed-customers`). */
export interface ManagedCustomer {
  id: string
  name: string
  status: CustomerStatus
}

/** Resposta de `GET` e `PUT /api/users/:id/managed-customers`, por nome. */
export interface ManagedCustomers {
  customers: ManagedCustomer[]
}

/**
 * Corpo de `PUT /api/users/:id/managed-customers` (Story 35.9): a lista INTEIRA dos
 * clientes de que a pessoa passa a ser gestora. Os que entram ficam com ela como gestora,
 * e os que saem ficam sem gestor. Um cliente com outro gestor responde 409, a menos que
 * `confirmTransfer` venha `true`.
 */
export const ManagedCustomersUpdateSchema = z
  .object({
    customerIds: z
      .array(z.string().min(1))
      .max(500)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: 'A lista de clientes não pode repetir um cliente.',
      }),
    confirmTransfer: z.boolean().optional(),
  })
  .strict()
export type ManagedCustomersUpdate = z.infer<typeof ManagedCustomersUpdateSchema>

/** Um cliente do pedido que já tem outro gestor de conta: o corpo do 409. */
export interface ManagedCustomerConflict {
  customerId: string
  customerName: string
  managerUserId: string
  managerName: string
}
